import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';
import {
  Alert, Button, Input, Select, Spin,
} from 'antd';
import DatePicker from '../DatePicker';
import EhrField from '../ehr/EhrField';
import EmrProviderAutocomplete from '../autocomplete/EmrProviderAutocomplete';
import { hasPresentValue, parsePrefillDate } from '../../util/ehr';
import cancelledRequest from '../../utils/cancelledRequest';
import './nextgenDocumentMetadataFields.scss';

// Surfaced verbatim as the blocking reason when a required field has no
// selectable value: the user must never reach a server error for a field they
// cannot fill, and must be told which Super Admin action unblocks them.
const PROVIDER_EMPTY_STATE = 'No NextGen providers are enabled for this practice. Ask a Super Admin to import or enable a provider for this practice before uploading.';
const PICK_LIST_EMPTY_STATE = 'This required field has no selectable options. Ask a Super Admin to enable its picklist options or refresh the practice metadata before uploading.';

// The optional counterparts state the same fact without claiming a blocker that
// does not exist: an unfilled optional field still produces a complete envelope,
// so telling the user the upload is blocked would be wrong.
const PROVIDER_EMPTY_OPTIONAL = 'No NextGen providers are enabled for this practice. This field is optional, so the upload can proceed without it.';
const PICK_LIST_EMPTY_OPTIONAL = 'This field has no selectable options. It is optional, so the upload can proceed without it.';

const datePrefill = (value) => parsePrefillDate(value)?.format('YYYY-MM-DD');

// The credential's referring provider field (ENG-3812): a Free Text field the
// server marks with this kind. It is filled from the referring provider
// directory, and its value is the picked provider's NextGen id.
const REFERRING_PROVIDER_KIND = 'referring_provider';

// Which per-practice provider fact governs a provider field. Every provider
// dropdown this editor renders is `kind: 'rendering_provider'` (the normalizer
// collapses NextGen's "provider"/"rendering provider" semantics into that one
// kind); `is_paq` then distinguishes the PAQ Provider field, which the field
// normalizer flags from the field's own description. Any other field kind has
// no provider list at all, so nothing narrows it.
const narrowingFlagFor = (field) => {
  if (field.kind !== 'rendering_provider') return null;

  return field.is_paq ? 'paq_enabled' : 'rendering_at_practice';
};

// Narrowing is guidance, never a gate. The server accepts any available
// provider, so three rules keep a narrowed list from becoming a dead end:
//
// 1. `null` is not `false`. A provider imported before these flags existed
//    carries `null` — "not yet observed" — and must not be narrowed away, even
//    when it sits alongside providers the source has flagged true. Only an
//    explicit `false` — the source saying no — narrows a provider away.
// 2. If narrowing leaves nothing, it is abandoned entirely. A practice whose
//    providers are all unflagged (or all flagged false) still gets the full
//    list rather than an empty dropdown.
// 3. A provider the user already picked stays in the list, so toggling the
//    show-all escape back off can never hide their own selection.
const narrowedProviders = (providers, flag, selectedId) => {
  if (!flag) return providers;

  const narrowed = providers.filter((provider) => (
    provider[flag] !== false || provider.id === selectedId
  ));

  return narrowed.length > 0 ? narrowed : providers;
};

// A provider field can only be filled from the practice-scoped provider list the
// read API returns, and a pick list only from its own approved entries, so an
// empty list makes either field unfillable rather than merely empty. Rendering
// an empty dropdown instead would leave a required field a silent dead end.
// Returns the message to surface, or null when the field is fillable.
const unfillableMessage = (documentType, field) => {
  if (field.kind === 'rendering_provider') {
    if ((documentType.providers || []).length > 0) return null;
    return field.required ? PROVIDER_EMPTY_STATE : PROVIDER_EMPTY_OPTIONAL;
  }

  if (field.kind === 'pick_list') {
    if ((field.options || []).length > 0) return null;
    return field.required ? PICK_LIST_EMPTY_STATE : PICK_LIST_EMPTY_OPTIONAL;
  }

  return null;
};

// Only a complete envelope is published. An incomplete one would let the drawer
// enable submission for a payload the server rejects, so the drawer is handed
// `undefined` until every required field holds a value.
const completeEnvelope = (documentType, fieldValues) => {
  if (!documentType) return undefined;

  const fields = documentType.fields || [];
  const missingRequired = fields.some((field) => field.required && !hasPresentValue(fieldValues[field.id]));
  if (missingRequired) return undefined;

  return {
    nextgen_document: {
      version: 1,
      practice_id: documentType.practice_id,
      document_type_id: documentType.id,
      source_digest: documentType.source_digest,
      fields: fields.filter((field) => hasPresentValue(fieldValues[field.id])).map((field) => ({
        id: field.id,
        value: fieldValues[field.id],
      })),
    },
  };
};

const NextgenDocumentMetadataFields = ({
  credentialId,
  dateOfServicePrefill,
  documentCategoryId,
  documentTitle,
  onAnalytics,
  onChange,
  onMarkAwaitingProvider,
  onReferringProviderField,
  onRequestProvider,
  practiceId,
  referringProviderNotes,
}) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(undefined);
  // The type the document's category is mapped to at this practice (Settings →
  // Categories), pre-selected once the list loads.
  const [mappedTypeId, setMappedTypeId] = useState(undefined);
  const [documentType, setDocumentType] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  // Per-field show-all escape from the PAQ/Rendering narrowing, keyed by field id.
  const [showAllProviders, setShowAllProviders] = useState({});
  // The directory entry picked in a referring provider field, by field id: the
  // search box shows that entry, while the field's value is its NextGen id.
  const [referringSelections, setReferringSelections] = useState({});
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [typesLoaded, setTypesLoaded] = useState(false);
  // `{ message, retryable, stage }`. Neither fetch can be re-run by repeating
  // the selection that started it — the type list has no in-drawer trigger at
  // all once the practice is fixed (a single-practice credential renders no
  // practice selector), and re-selecting the already selected type is a no-op —
  // so each failed fetch is retried through its own attempt counter.
  const [error, setError] = useState(null);
  const [listAttempt, setListAttempt] = useState(0);
  const [detailAttempt, setDetailAttempt] = useState(0);
  const onChangeRef = useRef(onChange);
  const onAnalyticsRef = useRef(onAnalytics);
  const onReferringProviderFieldRef = useRef(onReferringProviderField);
  const typeRequestRef = useRef(null);
  const detailRequestRef = useRef(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onAnalyticsRef.current = onAnalytics;
  }, [onAnalytics]);

  useEffect(() => {
    onReferringProviderFieldRef.current = onReferringProviderField;
  }, [onReferringProviderField]);

  // Told where the document type is set, so the drawer learns it in the same
  // pass rather than one render later.
  const reportReferringProviderField = useCallback((loadedType) => {
    onReferringProviderFieldRef.current?.(Boolean(
      loadedType?.fields?.some((field) => field.kind === REFERRING_PROVIDER_KIND),
    ));
  }, []);
  useEffect(() => () => onReferringProviderFieldRef.current?.(false), []);

  const publish = useCallback((nextDocumentType, nextValues) => {
    onChangeRef.current?.(completeEnvelope(nextDocumentType, nextValues));
  }, []);

  useEffect(() => {
    typeRequestRef.current?.abort();
    detailRequestRef.current?.abort();
    setDocumentTypes([]);
    setSelectedTypeId(undefined);
    setMappedTypeId(undefined);
    setDocumentType(null);
    reportReferringProviderField(null);
    setFieldValues({});
    setShowAllProviders({});
    setReferringSelections({});
    setTypesLoaded(false);
    setError(null);
    onChangeRef.current?.(undefined);

    if (!credentialId || !practiceId) {
      setLoadingTypes(false);
      return undefined;
    }

    const controller = new AbortController();
    typeRequestRef.current = controller;
    setLoadingTypes(true);

    axios.get('/api/v1/emr_options', {
      params: {
        emr_integration_credential_id: credentialId,
        source: 'nextgen/document_types',
        location: practiceId,
        document_category_id: documentCategoryId || undefined,
      },
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) return;
      const options = response.data.options || [];
      const mapped = response.data.mapped_document_type_id;
      setDocumentTypes(options);
      setTypesLoaded(true);
      // Only a type this practice still lists; the fields then load as for a pick.
      if (mapped && options.some((option) => option.value === mapped)) {
        setMappedTypeId(mapped);
        setSelectedTypeId(mapped);
        onAnalyticsRef.current?.('nextgen_document_type_preselected', { document_type_id: mapped });
      }
    }).catch((requestError) => {
      if (cancelledRequest(requestError, controller)) return;
      setError({
        message: requestError?.response?.data?.error || 'Unable to load NextGen document types.',
        retryable: true,
        stage: 'document_types',
      });
      onAnalyticsRef.current?.('nextgen_document_metadata_load_failed', { stage: 'document_types' });
    }).finally(() => {
      if (!controller.signal.aborted) setLoadingTypes(false);
    });

    return () => controller.abort();
  }, [credentialId, documentCategoryId, listAttempt, practiceId, reportReferringProviderField]);

  useEffect(() => {
    detailRequestRef.current?.abort();
    setDocumentType(null);
    reportReferringProviderField(null);
    setFieldValues({});
    setShowAllProviders({});
    setReferringSelections({});
    setError(null);
    onChangeRef.current?.(undefined);

    if (!selectedTypeId || !credentialId || !practiceId) {
      setLoadingFields(false);
      return undefined;
    }

    const controller = new AbortController();
    detailRequestRef.current = controller;
    setLoadingFields(true);

    axios.get(`/api/v1/nextgen_document_types/${encodeURIComponent(selectedTypeId)}`, {
      params: {
        emr_integration_credential_id: credentialId,
        location: practiceId,
      },
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) return;
      const loadedType = response.data.document_type;
      const initialValues = {};
      // Only prefill when the type has exactly one Date of Service field; with
      // several there is no unambiguous target.
      const dateFields = (loadedType.fields || []).filter((field) => field.is_date_of_service);
      const prefilledDate = dateFields.length === 1 ? datePrefill(dateOfServicePrefill) : undefined;
      if (prefilledDate) initialValues[dateFields[0].id] = prefilledDate;

      setDocumentType(loadedType);
      reportReferringProviderField(loadedType);
      setFieldValues(initialValues);
      publish(loadedType, initialValues);
    }).catch((requestError) => {
      if (cancelledRequest(requestError, controller)) return;
      setError({
        message: requestError?.response?.data?.error || 'Unable to load fields for this NextGen document type.',
        retryable: true,
        stage: 'document_type_fields',
      });
      onAnalyticsRef.current?.('nextgen_document_metadata_load_failed', { stage: 'document_type_fields' });
    }).finally(() => {
      if (!controller.signal.aborted) setLoadingFields(false);
    });

    return () => controller.abort();
  }, [credentialId, dateOfServicePrefill, detailAttempt, practiceId, publish, reportReferringProviderField, selectedTypeId]);

  const handleTypeChange = (nextTypeId) => {
    setSelectedTypeId(nextTypeId);
    onAnalyticsRef.current?.('nextgen_document_type_selected', {
      document_type_id: nextTypeId,
    });
  };

  // Paired with `nextgen_document_metadata_load_failed` so recovery from a
  // failed fetch is measurable rather than invisible. The error's stage picks
  // which fetch to re-run.
  const handleRetry = () => {
    if (error?.stage === 'document_types') {
      setListAttempt((attempt) => attempt + 1);
    } else {
      setDetailAttempt((attempt) => attempt + 1);
    }
    onAnalyticsRef.current?.('nextgen_document_metadata_retry_clicked', {
      stage: error?.stage,
    });
  };

  const handleFieldChange = (field, nextValue) => {
    const normalizedValue = moment.isMoment(nextValue)
      ? nextValue.format('YYYY-MM-DD')
      : nextValue?.target?.value ?? nextValue;
    const nextValues = { ...fieldValues, [field.id]: normalizedValue };
    setFieldValues(nextValues);
    publish(documentType, nextValues);
    // Field identifiers and kinds only: never a field label or the value the
    // user entered.
    onAnalyticsRef.current?.('nextgen_document_metadata_field_changed', {
      document_field_id: field.id,
      field_kind: field.kind,
      required: Boolean(field.required),
    });
  };

  const renderField = (field) => {
    const value = fieldValues[field.id];
    const fieldId = `nextgen-field-${field.id}`;
    const unfillable = unfillableMessage(documentType, field);
    // antd forwards `aria-*` onto the control the label points at (rc-select's
    // combobox input, rc-picker's text input, the native input), so the required
    // state the asterisk shows visually is announced too.
    const requiredAttr = field.required ? 'true' : undefined;
    let input;
    // How many providers the narrowing withheld from this field, if any.
    let narrowedAway = 0;

    if (unfillable) {
      input = <Alert type={field.required ? 'warning' : 'info'} showIcon message={unfillable} />;
    } else if (field.kind === 'date') {
      input = (
        <DatePicker
          data-testid={fieldId}
          id={fieldId}
          aria-required={requiredAttr}
          value={value ? moment(value, 'YYYY-MM-DD', true) : null}
          onChange={(nextValue) => handleFieldChange(field, nextValue)}
          format="MM/DD/YYYY"
          style={{ width: '100%' }}
          getPopupContainer={() => document.body}
          dropdownClassName="ehr-select-dropdown"
        />
      );
    } else if (field.kind === REFERRING_PROVIDER_KIND) {
      input = (
        <EmrProviderAutocomplete
          id={fieldId}
          required={Boolean(field.required)}
          credentialId={credentialId}
          placeholder="Search referring providers by name or NPI..."
          value={referringSelections[field.id] || ''}
          onChange={(slug, provider) => {
            setReferringSelections((current) => ({ ...current, [field.id]: slug }));
            handleFieldChange(field, (slug && provider?.emr_id) || '');
          }}
          onAnalytics={onAnalytics}
          onRequestProvider={onRequestProvider}
          onMarkAwaitingProvider={onMarkAwaitingProvider}
        />
      );
    } else if (field.kind === 'pick_list' || field.kind === 'rendering_provider') {
      const allProviders = documentType.providers || [];
      const narrowingFlag = showAllProviders[field.id] ? null : narrowingFlagFor(field);
      const providerOptions = narrowedProviders(allProviders, narrowingFlag, value);
      const options = field.kind === 'pick_list'
        ? (field.options || [])
        : providerOptions;
      // Only offered when narrowing actually withheld a provider — otherwise the
      // escape reveals nothing and is just noise.
      narrowedAway = allProviders.length - providerOptions.length;

      input = (
        <Select
          data-testid={fieldId}
          id={fieldId}
          aria-required={requiredAttr}
          showSearch
          allowClear
          value={value}
          onChange={(nextValue) => handleFieldChange(field, nextValue)}
          optionFilterProp="children"
          style={{ width: '100%' }}
          getPopupContainer={() => document.body}
          dropdownClassName="ehr-select-dropdown"
        >
          {options.map((option) => (
            <Select.Option key={option.id} value={option.id}>{option.description}</Select.Option>
          ))}
        </Select>
      );
    } else {
      input = (
        <Input
          data-testid={fieldId}
          id={fieldId}
          aria-required={requiredAttr}
          value={value || ''}
          onChange={(nextValue) => handleFieldChange(field, nextValue)}
          maxLength={field.max_length || undefined}
        />
      );
    }

    return (
      <EhrField
        key={field.id}
        controlId={fieldId}
        label={field.description}
        required={Boolean(field.required)}
      >
        {input}
        {field.kind === REFERRING_PROVIDER_KIND && referringProviderNotes}
        {(narrowedAway > 0 || showAllProviders[field.id]) && (
          <Button
            data-testid={`${fieldId}-show-all`}
            size="small"
            type="link"
            className="nextgen-document-metadata__show-all"
            onClick={() => {
              const nextShowAll = !showAllProviders[field.id];
              setShowAllProviders((current) => ({
                ...current,
                [field.id]: !current[field.id],
              }));
              // Counts and identifiers only: how often the narrowing is escaped,
              // and how much it was withholding when it was, never who was in
              // the list.
              onAnalyticsRef.current?.('nextgen_document_metadata_show_all_providers_clicked', {
                document_field_id: field.id,
                field_kind: field.kind,
                show_all: nextShowAll,
                provider_count: (documentType.providers || []).length,
                narrowed_away: narrowedAway,
              });
            }}
          >
            {showAllProviders[field.id]
              ? 'Show only providers configured for this practice'
              : `Show all ${(documentType.providers || []).length} practice providers`}
          </Button>
        )}
        {field.is_paq && (
          <div className="nextgen-document-metadata__help">
            This fills the document type&apos;s PAQ Provider metadata. Automatic Provider Approval Queue routing is controlled separately by NextGen System Administrator sign-off settings.
          </div>
        )}
      </EhrField>
    );
  };

  return (
    <div className={`nextgen-document-metadata${documentTitle ? ' nextgen-document-metadata--with-document-title' : ''}`}>
      {!practiceId && (
        <Alert type="info" showIcon message="Select a Practice / Location to load NextGen document types." />
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error.message}
          action={error.retryable ? (
            <Button
              data-testid="nextgen-document-metadata-retry"
              size="small"
              type="link"
              onClick={handleRetry}
            >
              Retry
            </Button>
          ) : undefined}
        />
      )}
      {practiceId && typesLoaded && documentTypes.length === 0 && !error && (
        <Alert
          type="warning"
          showIcon
          message="No approved NextGen document types are available for this practice. Ask a Super Admin to refresh and approve the practice catalog."
        />
      )}
      {practiceId && (
        <EhrField controlId="nextgen-document-type" label="Document Type" required>
          <Select
            data-testid="nextgen-document-type"
            id="nextgen-document-type"
            aria-required="true"
            showSearch
            allowClear
            value={selectedTypeId}
            onChange={handleTypeChange}
            loading={loadingTypes}
            disabled={loadingTypes}
            optionFilterProp="children"
            placeholder="Select a NextGen document type"
            style={{ width: '100%' }}
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          >
            {documentTypes.map((option) => (
              <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
            ))}
          </Select>
          {mappedTypeId && selectedTypeId === mappedTypeId && (
            <div className="nextgen-document-metadata__help" data-testid="nextgen-document-type-mapped">
              Selected from this document&apos;s category mapping.
            </div>
          )}
        </EhrField>
      )}
      {documentTitle && (
        <EhrField controlId="nextgen-document-title" label={documentTitle.label}>
          <Input
            data-testid="nextgen-document-title"
            id="nextgen-document-title"
            value={documentTitle.value || ''}
            onChange={(event) => documentTitle.onChange(event.target.value)}
            placeholder={documentTitle.placeholder}
          />
        </EhrField>
      )}
      {loadingFields && <Spin size="small" tip="Loading document fields..." />}
      {!loadingFields && documentType && (documentType.fields || []).map(renderField)}
    </div>
  );
};

NextgenDocumentMetadataFields.propTypes = {
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dateOfServicePrefill: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  documentCategoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  documentTitle: PropTypes.shape({
    label: PropTypes.node.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.string,
  }),
  onAnalytics: PropTypes.func,
  onChange: PropTypes.func,
  // The referring provider field's search footer and the notes under it, owned
  // by the drawer (ENG-3658/3659). Each link is offered only when given.
  onMarkAwaitingProvider: PropTypes.func,
  onReferringProviderField: PropTypes.func,
  onRequestProvider: PropTypes.func,
  practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  referringProviderNotes: PropTypes.node,
};

NextgenDocumentMetadataFields.defaultProps = {
  credentialId: null,
  dateOfServicePrefill: null,
  documentCategoryId: null,
  documentTitle: null,
  onAnalytics: () => {},
  onChange: () => {},
  onMarkAwaitingProvider: null,
  onReferringProviderField: null,
  onRequestProvider: null,
  practiceId: null,
  referringProviderNotes: null,
};

export { completeEnvelope, datePrefill, PROVIDER_EMPTY_STATE };
export default NextgenDocumentMetadataFields;
